/**
 * Collection-native adapter for the shared export pipeline
 * (matrixFilter/utils.js: generatePDFContent / exportToDOCX / captureMapCanvas*).
 *
 * The model path's `prepareExportData` reads model-specific context (project,
 * pickedTask, hexagonInfo) that doesn't exist for a collection. Rather than
 * bend that function to accept two shapes, this module builds the exact same
 * `exportData` object shape it returns, sourced from collection-native inputs:
 *   - collection name/uuid instead of project/pickedTask/hexagonInfo,
 *   - COLLECTION_REPORT_LABELS, so the shared templates say "objects" not "classes",
 *   - `resolution` (hexagon cell size, m²) as `hexagonSize`,
 *   - `classificationResults` from useHexagonClassification.results (mapping
 *     each class's detected `objectArea` → the shared shape's `area`/`totalArea`,
 *     so the report reproduces the panel's cost arithmetic; the hook's container
 *     `area` accumulator travels alongside as `containerArea` and is printed as
 *     its own column but never priced),
 *   - `coverageGaps` from the same detectors the color-filter panel uses
 *     (getSimpleFilterCoverageGaps / getMatrixFilterCoverageGaps), and
 *   - detection stats from collectionHexagonsSummary.detections.
 *
 * generatePDFContent/exportToDOCX are consumed as-is — they only read the
 * `exportData` shape, so this adapter is the entire integration surface.
 *
 * @module collectionExportData
 */

import {
	describeClassificationMethod,
	describeCoverageGaps,
} from '@routes/userRoutes/projects/singleProject/components/modifyLayers/hexagonColorFilter/matrixFilter/matrixClassification';
import { COLLECTION_REPORT_LABELS } from '@routes/userRoutes/projects/singleProject/components/modifyLayers/hexagonColorFilter/matrixFilter/reportLabels';
import { collectionReportHeader } from './collectionReportHeader';
import { objectLabel } from './transformUtils';

/**
 * @param {Object} params
 * @param {Object} params.filter - active matrix/simple filter config
 * @param {Object} params.classificationResults - per-class results from
 *   useHexagonClassification
 *   (`{ [classId]: { count, area, objectArea, sharePercentage } }`) — `objectArea`
 *   is the priced quantity, `area` is the container total and is reported beside
 *   it as `containerArea` without ever entering a cost figure
 * @param {string} params.collectionName
 * @param {Object} params.project - current project (area, created_at)
 * @param {Array<{key: string, label: string}>} params.objects - the collection's detection objects
 * @param {number} params.hexagonSize - resolution (m² per cell)
 * @param {string} params.userName - display name of the current user (report "Analyst" field)
 * @param {string} params.projectNotes
 * @param {Object} params.currency - resolved currency ({ id, symbol, label })
 * @param {Array<{id,label,unit}>} params.availableProperties - property catalog
 *   for classificationMethod label resolution
 * @param {Object} params.coverageGaps - result of getSimpleFilterCoverageGaps /
 *   getMatrixFilterCoverageGaps (matching filter.type), computed by the caller
 * @param {{ detections: Object, cellCount: number }|null} params.summary - result
 *   of collectionHexagonsSummary(cells)
 * @returns {Object} exportData, matching the shape generatePDFContent/exportToDOCX expect,
 *   plus `pricingBasisNote` so the report states that cost figures are priced on
 *   detected object area rather than hexagon cell area, `percentageLabel` so the
 *   share column is headed "% of area" as in the panel, and
 *   `containerAreaLabel`/`areaLabel` (+ per-row `containerArea`,
 *   `totalContainerArea`) so the Classification Summary prints "Cell area" and
 *   "Object area" as separate columns
 */
export const prepareCollectionExportData = ({
	filter,
	classificationResults,
	collectionName,
	project,
	objects,
	hexagonSize,
	userName,
	projectNotes,
	currency,
	availableProperties,
	coverageGaps,
	summary,
}) => {
	// Per-class cost summary — same math as the model path, but priced on the
	// hook's detected `objectArea` rather than its container `area`, so the report
	// reproduces the panel's cost arithmetic instead of the hexagon resolution.
	// The outbound keys stay `area`/`totalArea`: that's the shared generators'
	// contract, so only the quantity behind them changes, never their names.
	const summaryData = (filter?.classes ?? []).map(cls => {
		const r = classificationResults?.[cls.id];
		const area = r?.objectArea || 0;
		const price = cls.pricePerM2 || 0;
		return {
			label: cls.label,
			color: cls.color,
			count: r?.count || 0,
			area,
			// The container cell area, printed as its own column beside the priced
			// `area` above — the same "Cell area" + "Object area" pair the panel's
			// results table shows. Carried separately so the priced quantity and the
			// quantity `percentage` is a share of are both visible, rather than the
			// report showing one number and a prose note about the other. Never read
			// by the cost arithmetic.
			containerArea: r?.area || 0,
			// Outbound key stays `percentage` — that's the generators' contract.
			percentage: r?.sharePercentage || 0,
			pricePerM2: price,
			totalCost: area * price,
		};
	});

	const classificationCost = summaryData.reduce((sum, s) => sum + s.totalCost, 0);
	const totalArea = summaryData.reduce((sum, s) => sum + s.area, 0);
	const totalContainerArea = summaryData.reduce((sum, s) => sum + s.containerArea, 0);

	const customPriceFields = (filter?.customPriceFields || []).map(f => ({
		label: f.label,
		price: f.price || 0,
		cost: totalArea * (f.price || 0),
	}));
	const customPriceTotalCost = customPriceFields.reduce((sum, f) => sum + f.cost, 0);
	const totalCost = classificationCost + customPriceTotalCost;

	// Detection stats — from the whole-collection summary (all detected classes,
	// not just the ones referenced by the active color-filter).
	const detections = summary?.detections ?? {};
	const totalObjectCount = Object.values(detections).reduce(
		(sum, det) => sum + (det.count || 0),
		0
	);

	let totalHeightSum = 0;
	let totalHeightCount = 0;
	const detectionStats = Object.entries(detections).map(([className, det]) => {
		if (Number.isFinite(det.mean_height) && det.count) {
			totalHeightSum += det.mean_height * det.count;
			totalHeightCount += det.count;
		}
		const percentage =
			totalObjectCount > 0 ? ((det.count / totalObjectCount) * 100).toFixed(1) : 0;
		return {
			name: objectLabel(objects, className),
			count: det.count || 0,
			meanHeight: Number.isFinite(det.mean_height) ? det.mean_height.toFixed(2) : null,
			percentage,
		};
	});
	const overallMeanHeight =
		totalHeightCount > 0 ? (totalHeightSum / totalHeightCount).toFixed(2) : null;

	// Report header identity: the collection's display name
	const collectionLabel = collectionName?.trim();

	const header = collectionReportHeader({ project, objects });

	const classificationMethod = describeClassificationMethod(filter, availableProperties);
	const coverageGapMessage = describeCoverageGaps(filter, coverageGaps);

	return {
		summaryData,
		totalCost,
		customPriceFields,
		customPriceTotalCost,
		totalArea,
		totalContainerArea,
		// Collection-native header fields, in place of the model's project/task info.
		projectTitle: collectionName || 'Collection Classification Report',
		projectArea: header.projectArea,
		projectCreated: header.projectCreated,
		userName: userName || 'Unknown',
		modelClasses: header.modelClasses,
		modelDescription: collectionLabel || '-',
		totalObjectCount,
		detectionStats,
		overallMeanHeight,
		labels: COLLECTION_REPORT_LABELS,
		resolution: header.gsd,
		hexagonSize,
		projectNotes,
		currency,
		classificationMethod,
		coverageGapMessage,
		// The panel captions its cost summary with the pricing basis. The columns
		// below now name both areas, so this note no longer has to carry that on its
		// own — it stays because it is the only place that says which of the two
		// columns the price is applied to, and which one the share is taken of.
		pricingBasisNote:
			"Priced on detected object area within each class's cells, not on the cells' footprint area. % of area is each class's share of total cell area, not of the object area beside it.",
		// `sharePercentage` is a share of container cell area (hook default basis),
		// while `area` holds object area — name the basis so the header matches the
		// panel's "% of area" column instead of a bare "%".
		percentageLabel: '% of area',
		// Two area columns, named as in the panel's results table: the hexagon cell
		// area the filter classifies on, then the detected object area every cost
		// figure is priced on. Base unit m² in both, matching the {currency}/m²
		// prices beside them (the panel's CostSummary keeps the base unit for the
		// same reason), so the printed multiplication reconciles with the printed
		// cost. Supplying `containerAreaLabel` is what turns the extra column on in
		// the shared generators; the model path omits it and prints one area.
		containerAreaLabel: 'Cell area (m²)',
		areaLabel: 'Object area (m²)',
	};
};

export default prepareCollectionExportData;
